<template>
    <div
        class="card relative flex w-full justify-between px-4 py-2 text-sm"
        data-test="file-row"
    >
        <!--
            The whole bar opens the file. A button stretched over the row rather
            than a click handler on the row itself: it keeps the real button
            semantics (focus, Enter/Space, a name for screen readers) while
            leaving the menu beside it clickable, which a button wrapping the
            whole row could not - a button inside a button is invalid markup.
        -->
        <button
            type="button"
            class="rounded-muval-2 absolute inset-0"
            :aria-label="`Open ${label}`"
            @click="emit('open')"
        ></button>

        <div class="pointer-events-none relative my-auto flex min-w-0 items-center gap-4">
            <div class="relative shrink-0">
                <SvgIcon
                    name="system-document"
                    class="text-muval-gray-3 size-8"
                />
                <span
                    v-if="extension"
                    class="absolute bottom-2 left-px rounded-xs px-1 text-[8px] leading-tight font-medium text-white uppercase"
                    :class="getExtensionBgClass(extension)"
                >
                    {{ extension }}
                </span>
            </div>
            <div class="flex min-w-0 flex-col items-start gap-y-1">
                <div class="flex min-w-0 flex-col items-start md:flex-row md:items-center md:gap-x-4">
                    <span class="max-w-40 overflow-hidden text-left text-ellipsis whitespace-nowrap md:max-w-full">
                        {{ label }}
                    </span>
                    <span
                        v-if="file.created_at"
                        class="text-muval-gray-3 shrink-0 text-sm"
                        >{{ uploadedAt }}</span
                    >
                </div>
                <div class="flex flex-wrap items-center gap-x-2 gap-y-1 empty:hidden">
                    <div
                        v-if="file.shared_with_field_staff"
                        class="card text-muval-gray-1 flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-xs"
                    >
                        <span class="bg-brand size-1.5 rounded-full" />
                        Shared with field staff
                    </div>
                    <div
                        v-for="tag in file.tags ?? []"
                        :key="tag"
                        class="card text-muval-gray-1 flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-xs"
                    >
                        <span
                            :style="{ backgroundColor: getColourFromText(tag) }"
                            class="size-1.5 rounded-full"
                        />
                        {{ capitalize(tag) }}
                    </div>
                </div>
            </div>
        </div>

        <div class="text-muval-gray-1 relative flex items-center text-xs">
            <!--
                MPDropdown rather than the tippy-based ThreeDotOption: it is
                reka-ui, the same primitive MPModal is, so its portal positions
                correctly when the list is inside a dialog. Tippy's popover lands
                off-screen there.
            -->
            <MPDropdown
                :open="menuOpen"
                @update:open="(open: boolean) => (menuOpen = open)"
            >
                <MPDropdownTrigger />
                <MPDropdownContent>
                    <MPDropdownItem
                        label="Download"
                        @select="emit('download')"
                    />
                    <slot
                        name="menu-items"
                        :run="runFromMenu"
                    />
                    <MPDropdownItem
                        v-if="canEdit"
                        label="Edit"
                        @select="runFromMenu($event, () => emit('edit'))"
                    />
                    <MPDropdownItem
                        v-if="canDelete"
                        label="Delete"
                        @select="runFromMenu($event, () => emit('delete'))"
                    />
                </MPDropdownContent>
            </MPDropdown>
        </div>
    </div>
</template>

<script setup lang="ts">
import MPDropdown from '@components/dropdown/MPDropdown.vue';
import MPDropdownContent from '@components/dropdown/MPDropdownContent.vue';
import MPDropdownItem from '@components/dropdown/MPDropdownItem.vue';
import MPDropdownTrigger from '@components/dropdown/MPDropdownTrigger.vue';
import SvgIcon from '@components/utilities/SvgIcon.vue';
import useDayJs from '@plugins/useDayJs';
import { useUserStore } from '@store/user';
import { getColourFromText } from '@utilities/colors';
import { getExtensionBgClass, getFileDisplayName, getFileExtension } from '@utilities/fileDisplay';

/**
 * One stored file, as every file list in the CRM presents it: a document icon
 * carrying the file's extension, the label it is filed under, when it was
 * uploaded, its sharing and tag chips, and a menu.
 */

const props = withDefaults(
    defineProps<{
        file: Record<string, any>;
        /** Overrides the label where a collection has a name of its own. */
        label?: string;
        canEdit?: boolean;
        canDelete?: boolean;
    }>(),
    {
        label: undefined,
        canEdit: false,
        canDelete: false,
    },
);

const emit = defineEmits<{ open: []; download: []; edit: []; delete: [] }>();

const dayjs = useDayJs();
const userStore = useUserStore();

const menuOpen = ref(false);

const label = computed(() => props.label || getFileDisplayName(props.file));
const extension = computed(() => getFileExtension(props.file));

const uploadedAt = computed(() => {
    const timezone = userStore.getTimezone;
    const at = dayjs(props.file.created_at);

    return (timezone ? at.tz(timezone) : at).format('DD/MM/YY h:mmA');
});

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

/**
 * Runs a menu action that opens a dialog. `preventDefault` keeps the menu from
 * closing on select - without it its dismissal lands as a pointer event outside
 * the dialog that has just opened, and reka-ui closes that dialog again the
 * instant it appears - so the menu is closed by hand instead.
 *
 * Exposed to the `menu-items` slot so a caller's own dialog-opening item can use
 * it too.
 */
const runFromMenu = (event: Event, run: () => void) => {
    event.preventDefault();
    menuOpen.value = false;
    run();
};
</script>
